/**
 * Landing Page V2 - Experience List Section
 *
 * Horizontal scrollable experience list with React Query
 * 100% Design System Integration - NO hardcoded values
 */

'use client';

import React from 'react';
import {
  typographyColors,
  backgroundColors,
  spacing,
  fontPrimary,
  borders,
  textStyles,
  stylePresets,
} from '@/design-system/utils';
import { typography } from '@/design-system/tokens';
import Image from 'next/image';
import Link from 'next/link';
import { useFeaturedExperiences } from '@/hooks/useExperiences';
import type { Experience } from '@/services/experienceService';
import { getExperienceImage } from '@/services/experienceService';
import { getCategoryMeta } from '@/constants/experienceCategoryMeta';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ExperienceTypeBadge } from '@/components/ui/experience-type-badge';
import { useMediaQuery } from '@/hooks/useMediaQuery';

// Fallback experiences if API fails (not used - sections hide if no API data)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const fallbackExperiences: Experience[] = [
  {
    _id: '1',
    name: 'Ranthambore Safari',
    title:
      'Ranthambore Safari & Wildlife Experience – Tigers, Forts & Lakeside Magic',
    slug: 'ranthambore-safari',
    code: 'FB-001',
    type: 'activity',
    category: {
      title: 'Activities',
      description: 'Wildlife safari experience',
      _id: 'cat-1',
    },
    location: {
      _id: 'loc-1',
      name: 'Ranthambore',
      state: 'Rajasthan',
      city: 'Sawai Madhopur',
    },
    imageUrl: ['/assets/images/activity/activity-1.png'],
    averageRating: 0,
    reviewCount: 0,
    isActive: true,
    curated: true,
    createdAt: new Date().toISOString(),
  },
  {
    _id: '2',
    name: 'Paris Getaway',
    title: 'A romantic Paris Getaway - 7 Days / 6 Nights',
    slug: 'paris-getaway',
    code: 'FB-002',
    type: 'package',
    category: {
      title: 'Package',
      description: 'Travel package',
      _id: 'cat-2',
    },
    location: {
      _id: 'loc-2',
      name: 'Paris',
      state: 'Île-de-France',
      city: 'Paris',
    },
    imageUrl: ['/assets/images/location/portugal-1.png'],
    averageRating: 0,
    reviewCount: 0,
    isActive: true,
    curated: true,
    createdAt: new Date().toISOString(),
  },
  {
    _id: '3',
    name: 'Leh Ride',
    title: 'Ride to Leh from Kerala with Royal Enfield',
    slug: 'leh-ride',
    code: 'FB-003',
    type: 'riders_corner',
    category: {
      title: 'Riders Corner',
      description: 'Motorcycle ride',
      _id: 'cat-3',
    },
    location: {
      _id: 'loc-3',
      name: 'Leh-Ladakh',
      state: 'Ladakh',
      city: 'Leh',
    },
    imageUrl: ['/assets/images/location/image-7.png'],
    averageRating: 0,
    reviewCount: 0,
    isActive: true,
    curated: true,
    createdAt: new Date().toISOString(),
  },
  {
    _id: '4',
    name: 'Kayaking',
    title: 'Clear Kayaking at Johannesburg',
    slug: 'johannesburg-kayaking',
    code: 'FB-004',
    type: 'activity',
    category: {
      title: 'Activities',
      description: 'Water sports',
      _id: 'cat-4',
    },
    location: {
      _id: 'loc-4',
      name: 'Johannesburg',
      state: 'Gauteng',
      city: 'Johannesburg',
    },
    imageUrl: ['/assets/images/activity/activity-2.png'],
    averageRating: 0,
    reviewCount: 0,
    isActive: true,
    curated: true,
    createdAt: new Date().toISOString(),
  },
  {
    _id: '5',
    name: 'Dubai Safari',
    title: 'Dubai Safari Trails for Family',
    slug: 'dubai-safari',
    code: 'FB-005',
    type: 'signature',
    category: {
      title: 'Signature',
      description: 'Premium experience',
      _id: 'cat-5',
    },
    location: {
      _id: 'loc-5',
      name: 'Dubai',
      state: 'Dubai',
      city: 'Dubai',
    },
    imageUrl: ['/assets/images/location/image-8.png'],
    averageRating: 0,
    reviewCount: 0,
    isActive: true,
    curated: true,
    createdAt: new Date().toISOString(),
  },
  {
    _id: '6',
    name: 'Tibet Bike Ride',
    title: 'Bike Ride through the Mountains of Tibet',
    slug: 'tibet-bike-ride',
    code: 'FB-006',
    type: 'package',
    category: {
      title: 'Package',
      description: 'Mountain biking',
      _id: 'cat-6',
    },
    location: {
      _id: 'loc-6',
      name: 'Tibet',
      state: 'Tibet',
      city: 'Lhasa',
    },
    imageUrl: ['/assets/images/location/image-9.png'],
    averageRating: 0,
    reviewCount: 0,
    isActive: true,
    curated: true,
    createdAt: new Date().toISOString(),
  },
];

export function ExperienceListSection() {
  // Fetch experiences using React Query
  const { data, isLoading, error } = useFeaturedExperiences();
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Intersection observer for animations
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Ref for scrollable container
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const isDraggingRef = React.useRef(false);
  const startXRef = React.useRef(0);
  const scrollLeftRef = React.useRef(0);
  const hasDraggedRef = React.useRef(false);
  const shouldPreventClickRef = React.useRef(false);

  // Debug logging
  React.useEffect(() => {
    console.log('=== ExperienceList Debug ===');
    console.log('isLoading:', isLoading);
    console.log('data:', data);
    console.log('data length:', data?.length);
    console.log('error:', error);
    console.log('=============================');
  }, [data, isLoading, error]);

  // Only use API data, don't show fallback
  // Hide section if no API data
  const hasApiData = data && Array.isArray(data) && data.length > 0;
  const experiences = hasApiData ? data : [];

  // Enable mouse drag scrolling
  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || isLoading) return;

    const handleMouseDown = (e: MouseEvent) => {
      // Check if container has horizontal overflow
      const hasHorizontalScroll = container.scrollWidth > container.clientWidth;
      if (!hasHorizontalScroll) {
        return;
      }

      // Allow dragging even on links, but we'll prevent navigation if dragged
      isDraggingRef.current = true;
      hasDraggedRef.current = false;
      const rect = container.getBoundingClientRect();
      startXRef.current = e.clientX - rect.left;
      scrollLeftRef.current = container.scrollLeft;

      container.style.cursor = 'grabbing';
      container.style.userSelect = 'none';

      // Prevent default to avoid text selection and link navigation
      e.preventDefault();
      e.stopPropagation();
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;

      e.preventDefault();
      e.stopPropagation();

      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const walk = x - startXRef.current; // Distance moved

      // Only mark as dragged if movement is significant (more than 5px)
      if (Math.abs(walk) > 5) {
        hasDraggedRef.current = true;
        shouldPreventClickRef.current = true;
      }

      // Update scroll position
      // Dragging right (positive walk) should scroll right (increase scrollLeft)
      // Dragging left (negative walk) should scroll left (decrease scrollLeft)
      const newScrollLeft = scrollLeftRef.current - walk;
      container.scrollLeft = newScrollLeft;
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;

      if (container) {
        container.style.cursor = 'grab';
        container.style.userSelect = '';
      }

      // Reset hasDragged after a short delay to allow click handler to check it
      if (hasDraggedRef.current) {
        // Keep the flag for a bit to prevent click
        setTimeout(() => {
          hasDraggedRef.current = false;
          shouldPreventClickRef.current = false;
        }, 200);
      } else {
        // No drag occurred, allow click
        shouldPreventClickRef.current = false;
        hasDraggedRef.current = false;
      }
    };

    const handleClick = (e: MouseEvent) => {
      // Prevent navigation if we dragged
      if (shouldPreventClickRef.current || hasDraggedRef.current) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
    };

    const handleMouseLeave = () => {
      isDraggingRef.current = false;
      hasDraggedRef.current = false;
      shouldPreventClickRef.current = false;
      if (container) {
        container.style.cursor = 'grab';
        container.style.userSelect = '';
      }
    };

    container.addEventListener('mousedown', handleMouseDown, {
      passive: false,
    });
    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseLeave);
    // Use capture phase to intercept clicks before they reach links
    container.addEventListener('click', handleClick, { capture: true });

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.removeEventListener('click', handleClick, { capture: true });
    };
  }, [isLoading, experiences.length]);

  console.log('ExperienceList - Final experiences count:', experiences.length);
  console.log(
    'ExperienceList - Using:',
    data && data.length > 0 ? 'API DATA' : 'FALLBACK'
  );

  // Hide section if no API data
  if (!hasApiData && !isLoading) {
    return null;
  }

  return (
    <motion.section
      ref={ref}
      className='landing-experience-list-section'
      style={{
        paddingTop: isMobile ? spacing[6] : 0, // Extra top spacing on mobile to prevent overlap with suggestions card above
        paddingBottom: isMobile ? spacing[6] : spacing[8], // More padding on mobile
        paddingLeft: isMobile ? spacing[6] : spacing[4], // More padding on mobile
        paddingRight: isMobile ? spacing[6] : spacing[4], // More padding on mobile
        flex: '1 1 auto',
        minHeight: '30vh',
        maxHeight: '40vh',
        overflow: 'hidden',
      }}
    >
      <div
        className='flex flex-col items-center justify-center'
        style={{ gap: isMobile ? spacing[3] : spacing[1] }} // More gap on mobile
      >
        {/* Experience Cards Container */}
        <div
          className='hidden md:flex'
          style={{
            backgroundColor: backgroundColors.primary,
            borderColor: backgroundColors.shade,
            borderWidth: borders.width.thin,
            borderStyle: 'solid',
            padding: spacing[1],
            borderRadius: borders.radius['2xl'],
            width: stylePresets.quickLinksContainer.width,
            maxWidth: '100%',
            overflow: 'hidden', // Prevent parent from expanding
          }}
        >
          <div
            ref={scrollContainerRef}
            className='flex flex-nowrap overflow-x-auto overflow-y-clip relative scrollbar-hide'
            style={{
              gap: spacing[2],
              width: '100%',
              borderRadius: borders.radius.lg,
              scrollBehavior: 'auto', // Use 'auto' for instant scroll during drag
              WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS/Safari
              // Ensure content can overflow
              minWidth: 0, // Allow flex item to shrink below content size
              // Force horizontal scrolling
              display: 'flex',
              flexDirection: 'row',
              cursor: 'grab', // Show grab cursor to indicate draggable
              overflowX: 'auto', // Explicitly set horizontal overflow
              overflowY: 'hidden', // Prevent vertical overflow
            }}
          >
            {isLoading ? (
              // Loading skeleton
              <>
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div
                    key={i}
                    className='flex-shrink-0 animate-pulse'
                    style={{
                      width: stylePresets.quickLinkCard.width,
                      height: stylePresets.quickLinkCard.height,
                      backgroundColor: backgroundColors.secondary,
                      borderRadius: borders.radius.lg,
                    }}
                  />
                ))}
              </>
            ) : (
              experiences.map((experience, index) => (
                <motion.div
                  key={experience._id || experience.id}
                  className='flex-shrink-0'
                  initial={{ opacity: 0, y: 50 }}
                  animate={
                    inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }
                  }
                  transition={{
                    duration: 0.5,
                    ease: 'easeOut',
                    delay: index * 0.15, // 150ms delay - one by one animation
                  }}
                >
                  <ExperienceCard {...experience} />
                </motion.div>
              ))
            )}
          </div>
        </div>

        {/* Explore Button */}
        <Link
          href='/experiences'
          className='flex flex-col items-center justify-center cursor-pointer hover:opacity-80 transition-opacity'
          style={{
            gap: spacing[1],
            paddingLeft: spacing[2],
            paddingRight: spacing[2],
            paddingTop: spacing[1],
            paddingBottom: spacing[1],
            borderRadius: borders.radius.sm,
            marginTop: isMobile ? spacing[6] : 0, // Extra top margin on mobile to clear suggestions card above
          }}
        >
          <p
            style={{
              ...textStyles.bodySemibold,
              textAlign: 'center',
              lineHeight: 'normal',
            }}
          >
            Explore Nexgo Experiences
          </p>
          <div
            className='relative shrink-0'
            style={{
              width: '32px',
              height: '32px',
            }}
          >
            <Image
              src='/assets/images/gif/scroll_down_arrow.gif'
              alt='Scroll Down'
              width={32}
              height={32}
              className='object-cover'
              unoptimized // Keep unoptimized for GIF animations
            />
          </div>
        </Link>
      </div>
    </motion.section>
  );
}

function ExperienceCard(experience: Experience) {
  const { _id, title, location, category, shortTitle, name, type, slug } =
    experience;

  // Get image from media array, with fallback to imageUrl
  const displayImage = getExperienceImage(experience);

  // Use location name if available, otherwise use city/state
  const displayLocation = location
    ? `${location.city || location.name}`
    : 'Location TBD';

  const categoryMeta = getCategoryMeta(type, category?.title);

  // Use shortTitle if available, otherwise title or name
  const displayTitle = shortTitle || title || name;

  // Determine route based on type
  const getDetailRoute = (expType: string): string => {
    switch (expType) {
      case 'package':
      case 'signature':
        return `/package-details/${slug}`;
      case 'stay_experience':
        return `/stay-experience-details/${_id}`;
      case 'activity':
        return `/activity-details/${slug}`;
      case 'riders_corner':
        return `/riders-corner/${_id}`;
      default:
        return `/package-details/${slug}`; // Default fallback
    }
  };

  const detailRoute = getDetailRoute(type);

  return (
    <Link href={detailRoute}>
      <div
        className='relative overflow-hidden flex-shrink-0 cursor-pointer hover:opacity-90 transition-opacity'
        style={{
          width: stylePresets.quickLinkCard.width,
          height: stylePresets.quickLinkCard.height,
          borderRadius: borders.radius.lg,
        }}
      >
        {/* Background Image */}
        {displayImage && (
          <Image
            src={displayImage}
            alt={displayTitle}
            fill
            className='object-cover'
            style={{ borderRadius: borders.radius.lg }}
          />
        )}

        {/* Gradient Overlay */}
        <div
          className='absolute inset-0'
          style={{
            background:
              'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.5) 55.769%, rgba(0,0,0,0.8) 100%)',
            borderRadius: borders.radius.lg,
          }}
        />

        {/* Content */}
        <div
          className='relative h-full flex flex-col justify-between'
          style={{
            paddingLeft: spacing[3],
            paddingRight: spacing[3],
            paddingTop: spacing[2],
            paddingBottom: spacing[2],
            borderRadius: borders.radius.lg,
          }}
        >
          {/* Badge Container - Top Right */}
          <div
            className='flex flex-col items-end justify-center w-full'
            style={{ gap: spacing[2] }}
          >
            <ExperienceTypeBadge
              label={categoryMeta.label}
              icon={categoryMeta.icon}
              iconBackground='rgba(241, 241, 239, 0.19)'
              iconSize='20px'
              useImageIcon={true}
            />
          </div>

          {/* Title and Location - Bottom */}
          <div
            className='flex flex-col items-start justify-center'
            style={{
              gap: spacing[0.5],
              lineHeight: 'normal',
              color: typographyColors['alt-offset'],
            }}
          >
            <p
              className='overflow-ellipsis overflow-hidden w-full line-clamp-2'
              style={{
                fontFamily: fontPrimary,
                fontSize: typography.size.base,
                fontWeight: typography.weight.semibold,
                margin: 0,
                lineHeight: '1.4',
                minHeight: '44.8px', // 16px (base) * 1.4 * 2 lines = 44.8px
              }}
            >
              {displayTitle}
            </p>
            <p
              className='overflow-ellipsis overflow-hidden w-full'
              style={{
                fontFamily: fontPrimary,
                fontSize: '12px',
                fontWeight: typography.weight.light,
                margin: 0,
              }}
            >
              {displayLocation}
            </p>
          </div>
        </div>
      </div>
    </Link>
  );
}
